import React, { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useLocation, useParams } from "react-router-dom";
import { BellRing, Clock3, Mail, ShieldAlert, Sparkles } from "lucide-react";
import { devError, devLog } from "../utils/devLog";
import {
  CUSTOM_CASE_COMING_SOON_FORMSPREE_ENDPOINT,
  CUSTOM_CASE_PRODUCT_PATH,
  formatSuspectDetails,
  getLastCustomOrderAccessToken,
  getLastCustomOrderEmail,
  loadStoredCustomCaseFormData,
} from "../utils/customCase";

const normalizeString = (value = "") => String(value || "").trim();

const buildSuspectSummary = () => {
  const formData = loadStoredCustomCaseFormData();
  return {
    formData,
    suspectSummary: Array.isArray(formData?.suspects)
      ? formData.suspects.map((suspect, index) => formatSuspectDetails(suspect, index)).join(" | ")
      : "",
  };
};

const CustomCaseComingSoonPage: React.FC = () => {
  const location = useLocation();
  const params = useParams();
  const [{ firstName, email, note }, setFormState] = useState({
    firstName: "",
    email: "",
    note: "",
  });
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [errorMessage, setErrorMessage] = useState("");

  const context = useMemo(() => {
    const { formData, suspectSummary } = buildSuspectSummary();
    const lastOrderAccessToken = getLastCustomOrderAccessToken();
    const lastOrderEmail = getLastCustomOrderEmail();
    const now = new Date();

    return {
      formData,
      suspectSummary,
      lastOrderAccessToken,
      lastOrderEmail,
      routePath: location.pathname,
      routeSearch: location.search,
      routeHash: location.hash,
      accessToken: normalizeString(params.accessToken),
      shareToken: normalizeString(params.shareToken),
      evidenceId: normalizeString(params.evidenceId),
      clientIsoTimestamp: now.toISOString(),
      clientLocalTimestamp: now.toLocaleString(),
    };
  }, [location.hash, location.pathname, location.search, params.accessToken, params.evidenceId, params.shareToken]);

  useEffect(() => {
    const fallbackFirstName = normalizeString(context.formData?.firstName);
    const fallbackEmail =
      normalizeString(context.formData?.email).toLowerCase() ||
      normalizeString(context.lastOrderEmail).toLowerCase();

    setFormState((current) => ({
      ...current,
      firstName: current.firstName || fallbackFirstName,
      email: current.email || fallbackEmail,
    }));

    devLog("route", "custom_case_coming_soon_entered", {
      route: location.pathname,
      hasFormspreeEndpoint: Boolean(CUSTOM_CASE_COMING_SOON_FORMSPREE_ENDPOINT),
      hasStoredFormData: Boolean(fallbackFirstName || fallbackEmail || context.suspectSummary),
      hasLastOrderAccessToken: Boolean(context.lastOrderAccessToken),
      hasAccessToken: Boolean(context.accessToken),
      hasShareToken: Boolean(context.shareToken),
    });
  }, [
    context.accessToken,
    context.formData?.email,
    context.formData?.firstName,
    context.lastOrderAccessToken,
    context.lastOrderEmail,
    context.shareToken,
    context.suspectSummary,
    location.pathname,
  ]);

  const isConfigured = Boolean(CUSTOM_CASE_COMING_SOON_FORMSPREE_ENDPOINT);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!isConfigured) {
      setStatus("error");
      setErrorMessage("Formspree is not configured yet.");
      devError("api", "custom_case_coming_soon_submit_blocked", {
        route: location.pathname,
        reason: "missing_formspree_endpoint",
      });
      return;
    }

    setStatus("submitting");
    setErrorMessage("");

    const payload = new FormData();
    payload.set("firstName", normalizeString(firstName));
    payload.set("email", normalizeString(email).toLowerCase());
    payload.set("note", normalizeString(note));
    payload.set("_subject", `Custom case coming soon request from ${normalizeString(email).toLowerCase() || "unknown"}`);
    payload.set("routePath", context.routePath);
    payload.set("routeSearch", context.routeSearch);
    payload.set("routeHash", context.routeHash);
    payload.set("accessToken", context.accessToken);
    payload.set("shareToken", context.shareToken);
    payload.set("evidenceId", context.evidenceId);
    payload.set("clientIsoTimestamp", context.clientIsoTimestamp);
    payload.set("clientLocalTimestamp", context.clientLocalTimestamp);
    payload.set("customCaseProductPath", CUSTOM_CASE_PRODUCT_PATH);
    payload.set("lastOrderAccessToken", context.lastOrderAccessToken);
    payload.set("lastOrderEmail", context.lastOrderEmail);
    payload.set("storedCity", normalizeString(context.formData?.city));
    payload.set("storedLocation", normalizeString(context.formData?.location));
    payload.set("storedSource", normalizeString(context.formData?.source));
    payload.set("storedSuspectCount", String(Array.isArray(context.formData?.suspects) ? context.formData.suspects.length : 0));
    payload.set("storedSuspects", context.suspectSummary);
    payload.set(
      "technicalIssueNote",
      "Customer joined the custom-case coming soon notification list. Notify them when custom cases are ready to launch."
    );

    devLog("api", "custom_case_coming_soon_submit_started", {
      route: location.pathname,
      hasAccessToken: Boolean(context.accessToken),
      hasShareToken: Boolean(context.shareToken),
      storedSuspectCount: Array.isArray(context.formData?.suspects) ? context.formData.suspects.length : 0,
    });

    try {
      const response = await fetch(CUSTOM_CASE_COMING_SOON_FORMSPREE_ENDPOINT, {
        method: "POST",
        headers: {
          Accept: "application/json",
        },
        body: payload,
      });

      if (!response.ok) {
        const responseText = await response.text();
        throw new Error(responseText || "formspree_request_failed");
      }

      setStatus("success");
      devLog("api", "custom_case_coming_soon_submit_succeeded", {
        route: location.pathname,
        email: normalizeString(email).toLowerCase(),
      });
    } catch (error: any) {
      const message = String(error?.message || "Unable to send your request right now.");
      setStatus("error");
      setErrorMessage(message);
      devError("api", "custom_case_coming_soon_submit_failed", {
        route: location.pathname,
        message,
      });
    }
  };

  return (
    <>
      <Helmet>
        <title>Custom Cases Coming Soon | Cold Case Game Kits</title>
        <meta
          name="description"
          content="Custom cases are coming soon. Leave your email and we will let you know when the feature is ready."
        />
      </Helmet>

      <div className="min-h-screen bg-[radial-gradient(circle_at_top,_rgba(159,18,57,0.18),_transparent_38%),linear-gradient(180deg,#09090f_0%,#11111a_45%,#17111b_100%)] text-slate-100">
        <div className="mx-auto flex min-h-screen max-w-6xl flex-col justify-center px-6 py-16 sm:px-8 lg:px-10">
          <div className="grid gap-10 lg:grid-cols-[1.1fr_0.9fr]">
            <section className="rounded-[2rem] border border-rose-500/20 bg-black/25 p-8 shadow-[0_30px_120px_rgba(0,0,0,0.35)] backdrop-blur md:p-10">
              <div className="inline-flex items-center gap-2 rounded-full border border-amber-400/30 bg-amber-300/10 px-4 py-2 text-xs font-semibold uppercase tracking-[0.22em] text-amber-100">
                <Sparkles className="h-4 w-4" />
                Coming Soon
              </div>

              <h1 className="mt-6 max-w-3xl font-heading text-4xl leading-tight text-white sm:text-5xl">
                Custom cold cases are coming soon.
              </h1>

              <p className="mt-5 max-w-2xl text-lg leading-8 text-slate-300">
                We are building a custom-case experience that lets you shape the suspects, setting, and story details
                around your group. Leave your email below and we will let you know when custom cases are ready.
              </p>

              <div className="mt-8 grid gap-4 sm:grid-cols-3">
                <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
                  <ShieldAlert className="h-5 w-5 text-rose-300" />
                  <p className="mt-3 text-sm font-semibold text-white">Made for your group</p>
                  <p className="mt-1 text-sm leading-6 text-slate-400">
                    Share the city, tone, or style you want and we can use that as interest signal while we prepare the launch.
                  </p>
                </div>
                <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
                  <Clock3 className="h-5 w-5 text-amber-200" />
                  <p className="mt-3 text-sm font-semibold text-white">Early access interest</p>
                  <p className="mt-1 text-sm leading-6 text-slate-400">
                    Join the notification list now and we will let you know as soon as the feature is ready.
                  </p>
                </div>
                <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
                  <BellRing className="h-5 w-5 text-emerald-200" />
                  <p className="mt-3 text-sm font-semibold text-white">We will notify you</p>
                  <p className="mt-1 text-sm leading-6 text-slate-400">
                    Use your preferred email and we will send an update when custom cases are ready.
                  </p>
                </div>
              </div>
            </section>

            <section className="rounded-[2rem] border border-white/10 bg-slate-950/70 p-8 shadow-[0_24px_80px_rgba(0,0,0,0.4)] backdrop-blur md:p-10">
              <div className="flex items-center gap-3">
                <Mail className="h-5 w-5 text-rose-300" />
                <h2 className="text-xl font-semibold text-white">Notify Me When It Launches</h2>
              </div>

              <p className="mt-3 text-sm leading-6 text-slate-400">
                Leave your email and any notes about the kind of case you want. We will let you know when custom cases
                are ready.
              </p>

              <form className="mt-8 space-y-5" onSubmit={handleSubmit}>
                <div>
                  <label className="mb-2 block text-sm font-medium text-slate-200" htmlFor="coming-soon-first-name">
                    First name
                  </label>
                  <input
                    id="coming-soon-first-name"
                    className="w-full rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-white outline-none transition focus:border-rose-400/50 focus:bg-white/7"
                    value={firstName}
                    onChange={(event) => setFormState((current) => ({ ...current, firstName: event.target.value }))}
                    placeholder="Your first name"
                    autoComplete="given-name"
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-slate-200" htmlFor="coming-soon-email">
                    Email
                  </label>
                  <input
                    id="coming-soon-email"
                    type="email"
                    required
                    className="w-full rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-white outline-none transition focus:border-rose-400/50 focus:bg-white/7"
                    value={email}
                    onChange={(event) => setFormState((current) => ({ ...current, email: event.target.value }))}
                    placeholder="you@example.com"
                    autoComplete="email"
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-slate-200" htmlFor="coming-soon-note">
                    What kind of custom case would you want?
                  </label>
                  <textarea
                    id="coming-soon-note"
                    className="min-h-[132px] w-full rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-white outline-none transition focus:border-rose-400/50 focus:bg-white/7"
                    value={note}
                    onChange={(event) => setFormState((current) => ({ ...current, note: event.target.value }))}
                    placeholder="Example: I would love a small-town case for four suspects with a moody modern feel."
                  />
                </div>

                <button
                  type="submit"
                  disabled={!isConfigured || status === "submitting"}
                  className="inline-flex w-full items-center justify-center rounded-2xl bg-rose-700 px-5 py-3 text-sm font-semibold text-white transition hover:bg-rose-600 disabled:cursor-not-allowed disabled:bg-slate-700"
                >
                  {status === "submitting" ? "Sending..." : "Notify Me"}
                </button>
              </form>

              {status === "success" ? (
                <div className="mt-5 rounded-2xl border border-emerald-400/20 bg-emerald-400/10 px-4 py-3 text-sm text-emerald-100">
                  You are on the list. We will let you know when custom cases are ready.
                </div>
              ) : null}

              {status === "error" ? (
                <div className="mt-5 rounded-2xl border border-rose-400/20 bg-rose-500/10 px-4 py-3 text-sm text-rose-100">
                  {errorMessage || "Unable to send the request right now."}
                </div>
              ) : null}

              {!isConfigured ? (
                <div className="mt-5 rounded-2xl border border-amber-400/20 bg-amber-400/10 px-4 py-3 text-sm text-amber-100">
                  Missing `VITE_CUSTOM_CASE_COMING_SOON_FORMSPREE_ENDPOINT`.
                </div>
              ) : null}
            </section>
          </div>
        </div>
      </div>
    </>
  );
};

export default CustomCaseComingSoonPage;
